import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import React, { Fragment, useCallback, useState } from 'react';

interface ChatProps {
	title?: React.ReactNode;
	className?: string;
	children?: React.ReactNode;
}

/**
 * Custom hook to return a modal with its handler functions,
 * the returned modal has a default layout
 *
 * @returns
 * 		Modal: Default Modal element that will be rendered
 * 		isOpen: usual modal prop that says when the modal should appear
 * 		show: Handler function to show the modal
 * 		hide: Handler function to hide the modal
 */

const useChatBox = (): {
	ChatBox: React.FC<ChatProps>;
	isOpen: boolean;
	show: () => void;
	hide: () => void;
} => {
	const [isOpen, setIsOpen] = useState(false);

	const show = (): void => {
		setIsOpen(true);
	};

	const hide = (): void => {
		setIsOpen(false);
	};

	const ChatBox: React.FC<ChatProps> = useCallback(
		(props) => (
			<Transition.Root appear show={isOpen} as={Fragment}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0 scale-95"
					enterTo="opacity-100 scale-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100 scale-100"
					leaveTo="opacity-0 scale-95"
				>
					<div
						className={clsx(
							'transform transition-all z-[60] bg-white',
							'absolute rounded-2xl p-4',
							props.className
						)}
						style={{
							bottom: '4.5rem',
							right: '0',
							border: '1px solid #C0D653',
							boxShadow: '0px 2px 5px 0px #0000003D',
						}}
					>
						{props.title}

						{props.children}
					</div>
				</Transition.Child>
			</Transition.Root>
		),
		[isOpen]
	);

	return { ChatBox, isOpen, show, hide };
};

export default useChatBox;
